import type {
    FlowOrderItem,
    FlowNode,
    FlowEdge,
    SchemaSection,
    Section,
    Decision,
    Condition,
    ConditionGroup,
    ContextVariable,
    ContextItem,
} from '@app/services/aiFlow.types';

function insertContextDefaults(
    text: string,
    context: { name: string; defaultValue?: string }[]
): string {
    // Replace {{ variable_name }} with {{ context.variable_name | default('value') }} if default exists
    return text.replace(/{{\s*(\w+)\s*}}/g, (_, varName) => {
        const ctx = context.find((c) => c.name === varName);
        if (ctx && ctx.defaultValue) {
            return `{{ context.${varName} | default('${ctx.defaultValue}') }}`;
        }
        return `{{ context.${varName} }}`;
    });
}

function isSection(s: SchemaSection | undefined): s is Section {
    // Section has questions: Question[] and visibility_conditions not of type MetadataCondition
    return (
        !!s &&
        Array.isArray(s.questions) &&
        (!s.visibility_conditions ||
            !('ref' in s.visibility_conditions && s.visibility_conditions.ref.type === 'metadata'))
    );
}

function groupConditions(
    edges: FlowEdge[],
    flowOrder: FlowOrderItem[],
    mappedIds: Record<string, string>
) {
    const conditions: Record<string, Condition[]> = {};
    if (edges.length) {
        edges.forEach((e) => {
            const { source, sourceHandle, targetHandle } = e;
            if (!conditions[targetHandle]) {
                conditions[targetHandle] = [];
            }
            const targetNum = flowOrder.find((f) => f.id === source)!.number;
            const [type, value] = sourceHandle.split('-');
            let condition: Condition;

            if (type === 'question') {
                condition = {
                    ref: {
                        id: targetNum,
                        type: 'question',
                    },
                    value: value.split(':')[1],
                    comparison: 'equals',
                };
            } else {
                const mappedTargetNum: string | undefined = mappedIds[targetNum];
                condition = {
                    ref: {
                        id: mappedTargetNum ?? targetNum,
                        type: 'section',
                    },
                    value: null,
                    comparison: 'section_is_completed',
                };
            }
            conditions[targetHandle].push(condition);
        });
    }
    return Object.values(conditions);
}

function createConditions(
    nodeId: string,
    edges: FlowEdge[],
    flowOrder: FlowOrderItem[],
    mappedIds: Record<string, string>
): null | Condition | ConditionGroup {
    const conditionEdges = edges.filter((e) => e.target === nodeId && e.type === 'secondary');
    if (nodeId === '7a') console.log({ nodeId, conditionEdges, mappedIds });
    const groupedConditions = groupConditions(conditionEdges, flowOrder, mappedIds);
    if (groupedConditions.length === 0) {
        return null;
    } else if (groupedConditions.length === 1) {
        // Only one group
        if (groupedConditions[0].length === 1) {
            // Only one condition in the group
            return groupedConditions[0][0];
        } else {
            // Multiple conditions in the group
            return { logical: 'and', conditions: groupedConditions[0] } as ConditionGroup;
        }
    } else {
        // Multiple groups
        const orConditions = groupedConditions.map((group) =>
            group.length === 1 ? group[0] : { logical: 'and', conditions: group }
        );
        return { logical: 'or', conditions: orConditions } as ConditionGroup;
    }
}

function createBaseSection(
    node: Extract<FlowNode, { type: 'action' | 'section' }>,
    flowNum: string,
    edges: FlowEdge[],
    flowOrder: FlowOrderItem[],
    mappedIds: Record<string, string>,
    context: ContextVariable[]
) {
    const { id, data } = node;
    const conditions = createConditions(id, edges, flowOrder, mappedIds);
    return {
        section_id: flowNum,
        section_name: insertContextDefaults(data.name, context),
        display_number: flowNum.replace(/\D/g, ''),
        section_description: insertContextDefaults(data.description, context),
        visibility_conditions: conditions,
    };
}

function createDecision(
    node: Extract<FlowNode, { type: 'decision' }>,
    flowNum: string,
    context: ContextVariable[]
) {
    const { name, decisionVariable, passed, failed } = node.data;
    const base = {
        display_number: flowNum,
        section_name: insertContextDefaults(name, context),
        questions: [] as [],
    };
    const baseCondition = {
        ref: {
            id: decisionVariable,
            key: 'passed' as const,
            type: 'metadata' as const,
        },
        comparison: 'equals' as const,
        value: true,
    };
    const fail: Decision = {
        ...base,
        section_id: `${base.display_number}a`,
        section_name: `${base.section_name} - Failed`,
        section_description: insertContextDefaults(failed, context),
        visibility_conditions: { ...baseCondition, value: false },
    };
    const pass: Decision = {
        ...base,
        section_id: `${base.display_number}b`,
        section_name: `${base.section_name} - Passed`,
        section_description: insertContextDefaults(passed, context),
        visibility_conditions: baseCondition,
    };
    const mappedIds: Record<string, string> = {};
    mappedIds[base.display_number] = `${base.display_number}b`; // Map decision node ID to the "passed" section ID

    return { sections: [fail, pass], mappedIds };
}

function createQuestion(
    node: Extract<FlowNode, { type: 'question' }>,
    qId: string,
    edges: FlowEdge[],
    flowOrder: FlowOrderItem[],
    mappedIds: Record<string, string>,
    context: ContextVariable[]
) {
    const { id, data } = node;
    const conditions = createConditions(id, edges, flowOrder, mappedIds);
    return {
        question_id: qId,
        question_text: insertContextDefaults(data.text, context),
        question_type: 'text' as const,
        options: data.options,
        required: data.required,
        skippable: data.skippable,
        spelling_sensitive: data.spelling_sensitive,
        ...(conditions ? { visibility_conditions: conditions } : {}),
    };
}

export function createSchema(
    name: string,
    flowOrder: FlowOrderItem[],
    nodes: FlowNode[],
    edges: FlowEdge[],
    context: ContextVariable[]
) {
    const sections: SchemaSection[] = [];
    let mappedDecisionIds = {};
    flowOrder.forEach((i) => {
        const node = nodes.find((n) => n.id === i.id);
        if (node) {
            const { type } = node;
            switch (type) {
                case 'action': {
                    const baseSection = createBaseSection(
                        node,
                        i.number,
                        edges,
                        flowOrder,
                        mappedDecisionIds,
                        context
                    );
                    sections.push({
                        ...baseSection,
                        questions: null,
                    });
                    break;
                }
                case 'decision': {
                    const { sections: decisionSections, mappedIds } = createDecision(
                        node,
                        i.number,
                        context
                    );
                    mappedDecisionIds = { ...mappedDecisionIds, ...mappedIds };
                    sections.push(...decisionSections);
                    break;
                }
                case 'section': {
                    const baseSection = createBaseSection(
                        node,
                        i.number,
                        edges,
                        flowOrder,
                        mappedDecisionIds,
                        context
                    );
                    sections.push({
                        ...baseSection,
                        questions: [],
                    });
                    break;
                }
                case 'question': {
                    const [sId, qId] = i.number.split('.');
                    const question = createQuestion(
                        node,
                        qId,
                        edges,
                        flowOrder,
                        mappedDecisionIds,
                        context
                    );
                    const section = sections.find((s) => s.section_id === sId);
                    if (isSection(section)) {
                        section.questions.push(question);
                    }
                    break;
                }
                default: {
                    throw new Error(`Unknown node type: ${type satisfies never}`);
                }
            }
        }
    });
    const contextSchema: ContextItem[] = context.map((c) => {
        const copy = { ...c } as Partial<ContextVariable>;
        delete copy.defaultValue;
        return copy as ContextItem;
    });
    const schema = {
        name: name,
        sections: sections,
        context_schema: contextSchema,
    };
    return schema;
}

export function exportJSON(
    version: string,
    flow: FlowOrderItem[],
    nodes: FlowNode[],
    edges: FlowEdge[],
    contextVariables: ContextVariable[]
) {
    const schema = createSchema(version, flow, nodes, edges, contextVariables);
    const json = JSON.stringify(schema, null, 2);
    const blob = new Blob([json], {
        type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${version.replace(' ', '-')}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export function getFlowOverview(flowOrder: FlowOrderItem[], nodes: FlowNode[], edges: FlowEdge[]) {
    const result = new Map();
    for (const flowItem of flowOrder) {
        const n = nodes.find((n) => n.id === flowItem.id);
        if (n) {
            const conditionEdges = edges.filter(
                (e) => e.target === flowItem.id && e.targetHandle.startsWith('condition-target')
            );
            const groupedConditions = conditionEdges.reduce(
                (acc, edge) => {
                    const groupNum = edge.targetHandle.split('-')[2];

                    const num = flowOrder.find((f) => f.id === edge.source)?.number;
                    const sourceArr = edge.sourceHandle.split('-');
                    let value = sourceArr[1];
                    if (sourceArr[1].startsWith('value')) {
                        value = sourceArr[1].split(':')[1];
                    }
                    const condition = { about: `${sourceArr[0]} ${num}`, value: value };
                    if (!acc[groupNum]) {
                        acc[groupNum] = [];
                    }
                    acc[groupNum].push(condition);
                    return acc;
                },
                {} as Record<string, { about: string; value: string }[]>
            );

            if (n.type === 'question') {
                const parentId = flowItem.number.split('.')[0];
                const parent = result.get(parentId);
                if (parent) {
                    parent.questions.push({
                        number: flowItem.number,
                        type: n.type,
                        text: n.data.text,
                        skippable: n.data.skippable,
                        spellingSensitive: n.data.spelling_sensitive,
                        required: n.data.required,
                        conditions: groupedConditions,
                    });
                }
            } else {
                result.set(flowItem.number, {
                    number: flowItem.number,
                    type: n.type,
                    text: n.data.name,
                    conditions: groupedConditions,
                    questions: [],
                });
            }
        }
    }
    return Array.from(result.values());
}
